import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import exifr from 'exifr';
import {
  Loader2,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  Upload,
  X,
  ImageIcon,
  Building2,
} from 'lucide-react';
import { resolveSrc, formatDate } from '../lib/media';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'mb_admin_token';

const emptyForm = {
  title: '',
  scope: '',
  start_date: '',
  duration: '',
  value_range: '',
  location: '',
  status: 'In Progress',
};

// ---- image helpers ----
const loadImage = (file) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = URL.createObjectURL(file);
  });

const compressImage = async (file, maxDim = 1920, quality = 0.82) => {
  if (!file.type || !file.type.startsWith('image/')) return file;
  try {
    const img = await loadImage(file);
    let { width, height } = img;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(img.src);
    return await new Promise((res) =>
      canvas.toBlob((b) => res(b || file), 'image/jpeg', quality)
    );
  } catch (e) {
    return file;
  }
};

// ---------------- Login ----------------
function Login({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      setError('Incorrect password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <form onSubmit={submit} className="w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="font-heading text-4xl text-text-main">MB</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mt-1">
            Construction
          </div>
          <p className="mt-6 font-mono text-xs uppercase tracking-widest text-text-muted">
            Admin Access
          </p>
        </div>
        <label className="block font-mono text-[10px] uppercase tracking-widest text-text-muted mb-2">
          Password
        </label>
        <input
          type="password"
          data-testid="admin-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-surface border border-border px-4 py-3 text-text-main focus:border-primary outline-none transition-colors"
          autoFocus
        />
        {error && <p className="mt-3 text-sm text-accent-warning">{error}</p>}
        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={busy}
          className="mt-6 w-full border border-primary text-primary py-3 font-mono text-xs uppercase tracking-widest hover:bg-primary hover:text-background transition-colors flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

// ---------------- Project form ----------------
function ProjectForm({ initial, onCancel, onSave, saving }) {
  const [form, setForm] = useState(initial || emptyForm);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="border border-border bg-surface p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="admin-label">Project Title</label>
          <input className="admin-input" value={form.title} onChange={set('title')} data-testid="pf-title" />
        </div>
        <div className="md:col-span-2">
          <label className="admin-label">Scope of Works (plain English)</label>
          <textarea
            className="admin-input min-h-[120px]"
            value={form.scope}
            onChange={set('scope')}
            data-testid="pf-scope"
          />
        </div>
        <div>
          <label className="admin-label">Start Date</label>
          <input type="date" className="admin-input" value={form.start_date} onChange={set('start_date')} data-testid="pf-start" />
        </div>
        <div>
          <label className="admin-label">Duration</label>
          <input className="admin-input" placeholder="e.g. 10 months (est.)" value={form.duration} onChange={set('duration')} data-testid="pf-duration" />
        </div>
        <div>
          <label className="admin-label">Project Value (range)</label>
          <input className="admin-input" placeholder="e.g. R6m – R8m" value={form.value_range} onChange={set('value_range')} data-testid="pf-value" />
        </div>
        <div>
          <label className="admin-label">Location</label>
          <input className="admin-input" value={form.location || ''} onChange={set('location')} data-testid="pf-location" />
        </div>
        <div>
          <label className="admin-label">Status</label>
          <input className="admin-input" value={form.status || ''} onChange={set('status')} data-testid="pf-status" />
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.title}
          data-testid="pf-save"
          className="border border-primary text-primary px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-primary hover:text-background transition-colors flex items-center gap-2 disabled:opacity-40"
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : 'Save Project'}
        </button>
        <button
          onClick={onCancel}
          className="border border-border text-text-muted px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:text-text-main transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------- Photo manager ----------------
function PhotoManager({ project, token, onChanged }) {
  const [status, setStatus] = useState('');
  const [caption, setCaption] = useState('');
  const authHeader = { Authorization: `Bearer ${token}` };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      setStatus(`Reading ${file.name}...`);
      let takenISO = null;
      try {
        const meta = await exifr.parse(file, ['DateTimeOriginal', 'CreateDate', 'ModifyDate']);
        const d = meta && (meta.DateTimeOriginal || meta.CreateDate || meta.ModifyDate);
        if (d) takenISO = new Date(d).toISOString();
      } catch (e) {
        /* no exif */
      }
      if (!takenISO && file.lastModified) takenISO = new Date(file.lastModified).toISOString();

      setStatus(`Uploading ${file.name}...`);
      const blob = await compressImage(file);
      const form = new FormData();
      const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
      form.append('file', blob, `${base}.jpg`);
      if (takenISO) form.append('taken_at', takenISO);
      if (caption) form.append('caption', caption);
      try {
        await axios.post(`${API}/admin/current-projects/${project.id}/photos`, form, {
          headers: authHeader,
        });
        toast.success(`Photo added & saved${caption ? `: ${caption}` : ''}`);
      } catch (e) {
        setStatus(`Failed to upload ${file.name}`);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setStatus('');
    setCaption('');
    onChanged();
  };

  const deletePhoto = async (photoId) => {
    await axios.delete(`${API}/admin/photos/${photoId}`, { headers: authHeader });
    toast.success('Photo deleted');
    onChanged();
  };

  const photos = project.photos || [];

  return (
    <div className="mt-5 border-t border-border pt-5">
      <div className="flex items-center gap-2 mb-4">
        <ImageIcon size={14} className="text-primary" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
          Progress Photos ({photos.length})
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          className="admin-input flex-1"
          placeholder="Caption for next upload (e.g. Roof trusses installed)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          data-testid={`caption-${project.id}`}
        />
        <label className="cursor-pointer inline-flex items-center gap-2 border border-primary text-primary px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-primary hover:text-background transition-colors whitespace-nowrap">
          <Upload size={14} /> Add Photos
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid={`upload-${project.id}`}
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />
        </label>
      </div>
      {status && <p className="text-xs text-primary mb-4 font-mono">{status}</p>}
      <p className="text-[11px] text-text-muted mb-4">
        Photos are <span className="text-primary">saved automatically</span> the moment you upload
        them &mdash; no need to press Save. The capture date is read from each photo&apos;s metadata to
        order them by progress.
      </p>

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((ph) => (
            <div key={ph.id} className="relative group border border-border">
              <img src={resolveSrc(ph.url)} alt={ph.caption || ''} className="w-full h-28 object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-background/85 px-2 py-1">
                <div className="font-mono text-[9px] text-primary">{formatDate(ph.taken_at)}</div>
                {ph.caption && <div className="text-[10px] text-text-muted truncate">{ph.caption}</div>}
              </div>
              <button
                onClick={() => deletePhoto(ph.id)}
                className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-background/80 text-accent-warning opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete photo"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Dashboard ----------------
function Dashboard({ token, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const authHeader = { Authorization: `Bearer ${token}` };

  const loadProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/current-projects`);
      setProjects(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const createProject = async (form) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/current-projects`, form, { headers: authHeader });
      setCreating(false);
      toast.success('Project created');
      await loadProjects();
    } catch (e) {
      if (e.response && e.response.status === 401) onLogout();
    } finally {
      setSaving(false);
    }
  };

  const updateProject = async (id, form) => {
    setSaving(true);
    try {
      const { title, scope, start_date, duration, value_range, location, status } = form;
      await axios.put(
        `${API}/admin/current-projects/${id}`,
        { title, scope, start_date, duration, value_range, location, status },
        { headers: authHeader }
      );
      setEditingId(null);
      toast.success('Project details saved');
      await loadProjects();
    } catch (e) {
      if (e.response && e.response.status === 401) onLogout();
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project and all its photos?')) return;
    await axios.delete(`${API}/admin/current-projects/${id}`, { headers: authHeader });
    toast.success('Project deleted');
    await loadProjects();
  };

  const handleLogout = () => {
    if (creating || editingId) {
      const ok = window.confirm(
        'A project text form is still open. Any uploaded photos are already saved, but unsaved text changes will be lost. Log out anyway?'
      );
      if (!ok) return;
    }
    onLogout();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="text-primary" size={20} />
            <div>
              <div className="font-heading text-xl text-text-main leading-none">Current Projects</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                Admin Dashboard
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-text-muted hover:text-primary transition-colors font-mono text-xs uppercase tracking-widest"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            data-testid="new-project-btn"
            className="mb-8 flex items-center gap-2 border border-primary text-primary px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-primary hover:text-background transition-colors"
          >
            <Plus size={16} /> New Current Project
          </button>
        )}

        {creating && (
          <div className="mb-8">
            <ProjectForm onCancel={() => setCreating(false)} onSave={createProject} saving={saving} />
          </div>
        )}

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="animate-spin text-primary" size={28} />
          </div>
        ) : projects.length === 0 ? (
          <p className="text-text-muted">No current projects yet. Create your first one above.</p>
        ) : (
          <div className="space-y-6">
            {projects.map((p) => (
              <div key={p.id} className="border border-border bg-surface p-6" data-testid={`admin-project-${p.id}`}>
                {editingId === p.id ? (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 mb-3 text-primary">
                      <Pencil size={13} />
                      <span className="font-mono text-[10px] uppercase tracking-widest">
                        Editing project details &mdash; remember to press Save
                      </span>
                    </div>
                    <ProjectForm
                      initial={p}
                      onCancel={() => setEditingId(null)}
                      onSave={(form) => updateProject(p.id, form)}
                      saving={saving}
                    />
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-2xl text-text-main">{p.title}</h3>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-primary mt-1">
                        {p.status} · {p.value_range} · {p.duration}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setEditingId(p.id)}
                        data-testid={`edit-project-${p.id}`}
                        className="w-9 h-9 flex items-center justify-center border border-border text-text-muted hover:text-primary hover:border-primary transition-colors"
                        aria-label="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => deleteProject(p.id)}
                        className="w-9 h-9 flex items-center justify-center border border-border text-text-muted hover:text-accent-warning hover:border-accent-warning transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                )}
                {/* Photos are always manageable, regardless of text editing */}
                <PhotoManager project={p} token={token} onChanged={loadProjects} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------- Root ----------------
export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {
        if (active) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
